/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SvgIconRegistryService } from 'angular-svg-icon';
import { appConstants } from '@app/common/app-constants';
import { Title } from '@angular/platform-browser';
import { SignOutService } from './common/sign-out.service';
import { NavigationStart, Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LogEntry, LogLevel } from '@core/lib/services/log-entry';
import { icons } from './icons';
import { WindowRef } from '@app/common/window-ref.service';
import { InteractionLogService } from '@core/lib/log';
import { UtilService } from '@app/util/util.service';

export let browserRefresh = false;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  private translateObserver: Subscription;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private iconRegistry: SvgIconRegistryService,
    private title: Title,
    private signOutService: SignOutService,
    private httpClient: HttpClient,
    private windowRef: WindowRef,
    private interactionLogService: InteractionLogService,
    private util: UtilService
  ) {
    localStorage.redirectToLogin = 'false';
    // LOGGING Configuration
    LogEntry.setHttpClient(this.httpClient);
    LogEntry.setEndPoint(appConstants.appURLs.log);
    this.interactionLogService.configuration = this.windowRef.nativeWindow[`appConfig`] = {
      diDisable: false,
      logToServer: false,
      logLevel: LogLevel.ERROR,
      logPerformance: false, // indicates to log performance readings
      logInteractions: false, // indicates to log interaction readings
      performanceHooks: false,
      enableLineShaft: false
    };
    this.interactionLogService.endpoint = appConstants.appURLs.bulklog;
    this.interactionLogService.httpClient = this.httpClient;

    this.interactionLogService.startSendToServerInterval();
    this.registerIcons();
    this.translateObserver = forkJoin({
      prodName: this.translate.get('common.productName'),
      diagnosticUseMsg: this.translate.get('label.notForDiagnosticUse')
    }).subscribe(({ prodName, diagnosticUseMsg }) => {
      if (this.util.getSystemProp()[appConstants.system_properties.NOT_FOR_DIAGNOSTIC_USE_MESSAGE_ENABLED] === 'true') {
        this.title.setTitle(diagnosticUseMsg + ' - ' + prodName);
      } else {
        this.title.setTitle(prodName);
      }
    });
  }

  registerIcons() {
    for (const icon of icons) {
      this.iconRegistry.addSvg(icon.name, icon.data);
    }
  }

  ngOnInit(): void {
    this.subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.updateBrowserRefreshFlag(event.url);
      }
    });
  }

  private updateBrowserRefreshFlag(url: string): void {
    browserRefresh = !this.util.isSharedExamUrl(url) && !this.router.navigated;
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
    this.translateObserver && this.translateObserver.unsubscribe();
  }
}
